import type { PaymentSummaryDto } from '@repo/types';
import { PAYMENT_STATE_LABELS, PaymentFilterKey, PaymentSortBy } from '@repo/types';
import { PAYMENT_METHOD, PAYMENT_STATE } from '@theailab-dev/parchment';
import type { PaymentState } from '@theailab-dev/parchment';
import React, { useEffect, useState } from 'react';
import { HiSearch, HiRefresh } from 'react-icons/hi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Badge,
  Card,
  Button,
  Input,
  DateRangeFilter,
  SelectFilter,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePayments } from '../../hooks/usePayments';

const Payments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const searchKeyword = searchParams.get('search') || '';
  const urlFilterKey = (searchParams.get('filterKey') as PaymentFilterKey | null) || '';
  const urlFilterValue = searchParams.get('filterValue') || '';
  const urlStartDate = searchParams.get('startDate') || '';
  const urlEndDate = searchParams.get('endDate') || '';

  const [searchQuery, setSearchQuery] = useState(searchKeyword);
  const [startDate, setStartDate] = useState(urlStartDate);
  const [endDate, setEndDate] = useState(urlEndDate);
  const [selectedFilterKey, setSelectedFilterKey] = useState<'' | PaymentFilterKey.STATE | PaymentFilterKey.METHOD>(
    urlFilterKey === 'state' || urlFilterKey === 'method' ? urlFilterKey : '',
  );
  const [selectedFilterValue, setSelectedFilterValue] = useState(urlFilterValue);

  const effectiveFilterKey: PaymentFilterKey | undefined = searchKeyword
    ? PaymentFilterKey.SEARCH
    : selectedFilterKey && selectedFilterValue
      ? selectedFilterKey
      : undefined;

  const effectiveFilterValue: string | undefined = searchKeyword ? searchKeyword : selectedFilterValue || undefined;

  const queryParams = {
    page: currentPage,
    limit: currentLimit,
    sortBy: PaymentSortBy.CREATED_AT,
    sortOrder: 'DESC' as const,
    filterKey: effectiveFilterKey,
    filterValue: effectiveFilterValue,
    startDate: urlStartDate || undefined,
    endDate: urlEndDate || undefined,
  };

  const { data, isLoading, isError, error, refetch } = usePayments(queryParams);

  useEffect(() => {
    setSearchQuery(searchKeyword);
    setStartDate(urlStartDate);
    setEndDate(urlEndDate);
    setSelectedFilterKey(urlFilterKey === 'state' || urlFilterKey === 'method' ? urlFilterKey : '');
    setSelectedFilterValue(urlFilterValue);
  }, [searchKeyword, urlStartDate, urlEndDate, urlFilterKey, urlFilterValue]);

  useEffect(() => {
    if (!searchParams.has('page')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  const payments: PaymentSummaryDto[] = data?.data || [];
  const meta = data?.meta;
  const page = meta?.page || 1;
  const limit = meta?.limit || 20;
  const total = meta?.total || 0;
  const totalPages = meta?.totalPages || 1;

  const applyParamsWithDates = (baseParams: URLSearchParams) => {
    if (startDate) baseParams.set('startDate', startDate);
    if (endDate) baseParams.set('endDate', endDate);
    setSearchParams(baseParams);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    }
    applyParamsWithDates(params);
  };

  const handleDateChange = (type: 'startDate' | 'endDate', value: string) => {
    if (type === 'startDate') setStartDate(value);
    else setEndDate(value);
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set(type, value);
    } else {
      params.delete(type);
    }
    setSearchParams(params);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePrimaryFilterChange = (key: string) => {
    const newKey: '' | PaymentFilterKey.STATE | PaymentFilterKey.METHOD =
      key === 'state' ? PaymentFilterKey.STATE : key === 'method' ? PaymentFilterKey.METHOD : '';
    setSelectedFilterKey(newKey);
    setSelectedFilterValue('');

    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (searchKeyword) params.set('search', searchKeyword);
    if (newKey) params.set('filterKey', newKey);
    if (urlStartDate) params.set('startDate', urlStartDate);
    if (urlEndDate) params.set('endDate', urlEndDate);

    setSearchParams(params);
  };

  const handleSecondaryFilterChange = (value: string) => {
    setSelectedFilterValue(value);

    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (searchKeyword) params.set('search', searchKeyword);
    if (selectedFilterKey && value) {
      params.set('filterKey', selectedFilterKey);
      params.set('filterValue', value);
    }
    if (urlStartDate) params.set('startDate', urlStartDate);
    if (urlEndDate) params.set('endDate', urlEndDate);

    setSearchParams(params);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setSelectedFilterKey('');
    setSelectedFilterValue('');
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleRowClick = (id: number) => {
    void navigate(`/dashboard/payments/${id}`);
  };

  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    } catch {
      return '-';
    }
  };

  const getStateBadgeVariant = (state: PaymentState) => {
    switch (state) {
      case PAYMENT_STATE.SUCCEEDED:
        return 'success';
      case PAYMENT_STATE.PROCESSING:
        return 'primary';
      case PAYMENT_STATE.FAILED:
        return 'danger';
      case PAYMENT_STATE.READY:
      default:
        return 'neutral';
    }
  };

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '결제 목록을 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <TablePageLayout
      title="결제 목록"
      description="전체 결제 내역을 조회하고 관리하세요"
      action={null}
      filters={
        <>
          <SelectFilter
            value={selectedFilterKey}
            onChange={handlePrimaryFilterChange}
            options={[
              { value: 'state', label: '결제 상태' },
              { value: 'method', label: '결제 수단' },
            ]}
          />

          <SelectFilter
            value={selectedFilterValue}
            onChange={handleSecondaryFilterChange}
            disabled={!selectedFilterKey}
            placeholder={
              selectedFilterKey === 'state' ? '전체 상태' : selectedFilterKey === 'method' ? '전체 수단' : '필터 선택'
            }
            options={
              selectedFilterKey === 'state'
                ? [
                    { value: PAYMENT_STATE.READY, label: '대기' },
                    { value: PAYMENT_STATE.PROCESSING, label: '진행 중' },
                    { value: PAYMENT_STATE.SUCCEEDED, label: '결제 완료' },
                    { value: PAYMENT_STATE.FAILED, label: '실패' },
                  ]
                : selectedFilterKey === 'method'
                  ? [
                      { value: PAYMENT_METHOD.CARD, label: '카드' },
                      { value: PAYMENT_METHOD.VBANK, label: '가상계좌' },
                      { value: PAYMENT_METHOD.TRANSFER, label: '계좌이체' },
                      { value: PAYMENT_METHOD.COUPON, label: '쿠폰' },
                      { value: PAYMENT_METHOD.FREE, label: '무료' },
                    ]
                  : []
            }
          />

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(value) => handleDateChange('startDate', value)}
            onEndDateChange={(value) => handleDateChange('endDate', value)}
          />

          {(searchKeyword || selectedFilterKey || selectedFilterValue || startDate || endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              leftIcon={<HiRefresh />}
              className="text-slate-500">
              초기화
            </Button>
          )}
        </>
      }
      search={
        <>
          <div className="relative w-full lg:w-72">
            <Input
              placeholder="이메일, 주문번호 등으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              leftIcon={<HiSearch />}
              className="w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary">
            검색
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[4.5rem] whitespace-nowrap">주문서 ID</TableHead>
              <TableHead className="w-25 whitespace-nowrap">구매자</TableHead>
              <TableHead className="w-44">이메일</TableHead>
              <TableHead>상품명</TableHead>
              <TableHead className="w-30 whitespace-nowrap">결제 금액</TableHead>
              <TableHead className="w-30 whitespace-nowrap">상태</TableHead>
              <TableHead className="w-40 whitespace-nowrap">결제일시</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id} onClick={() => handleRowClick(payment.id)}>
                <TableCell className="whitespace-nowrap text-slate-500">#{payment.orderSheetId}</TableCell>
                <TableCell className="truncate text-slate-700" title={payment.buyerName}>
                  {payment.buyerName}
                </TableCell>
                <TableCell className="truncate text-slate-500" title={payment.buyerEmail}>
                  {payment.buyerEmail}
                </TableCell>
                <TableCell className="truncate text-slate-700" title={payment.orderTitle || undefined}>
                  {payment.orderTitle || '-'}
                </TableCell>
                <TableCell className="whitespace-nowrap text-slate-900">{payment.amount.toLocaleString()}원</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Badge variant={getStateBadgeVariant(payment.state)}>{PAYMENT_STATE_LABELS[payment.state]}</Badge>
                </TableCell>
                <TableCell nowrap className="text-slate-500">
                  {formatDateTime(payment.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && payments.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">결제 내역이 없습니다</h3>
          <p className="mt-1 text-slate-500">검색 조건을 변경하거나 기간을 다시 설정해보세요.</p>
          <Button variant="outline" className="mt-4" onClick={handleClearSearch}>
            필터 초기화
          </Button>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Payments;
